import type { Extent } from 'ol/extent';
import type { LayerModel, Selection, SelectionEntry, SelectionLevel, SelectionSet, VectorFeatureRecord, VectorLayerModel } from '../core/types';

export const emptySelectionSet = (): SelectionSet => ({ layerId: null, entries: [], focus: null });

export const selectionEntryKey = (entry: SelectionEntry): string => `${entry.source}:${entry.layerId}:${entry.kind}:${entry.id}`;

export function updateSelectionSet(current: SelectionSet, entry: SelectionEntry, additive: boolean): SelectionSet {
  if (!additive || current.layerId !== entry.layerId) return { layerId: entry.layerId, entries: [entry], focus: entry };
  const key = selectionEntryKey(entry);
  const exists = current.entries.some((item) => selectionEntryKey(item) === key);
  const entries = exists ? current.entries.filter((item) => selectionEntryKey(item) !== key) : [...current.entries, entry];
  return { layerId: entries.length ? entry.layerId : null, entries, focus: exists ? entries.at(-1) ?? null : entry };
}

export function replaceSelectionSet(entries: readonly SelectionEntry[]): SelectionSet {
  if (!entries.length) return emptySelectionSet();
  const layerId = entries[0]!.layerId;
  const unique = [...new Map(entries.filter((entry) => entry.layerId === layerId).map((entry) => [selectionEntryKey(entry), entry])).values()];
  return { layerId, entries: unique, focus: unique.at(-1) ?? null };
}

export function masterEntryForFragment(model: LayerModel | null, fragmentId: string, level: SelectionLevel): SelectionEntry | null {
  const fragment = model?.fragmentsById.get(fragmentId);
  if (!model || !fragment) return null;
  const id = level === 'finca' ? fragment.fincaId : level === 'fragment' ? fragment.id : level === 'tablon' ? fragment.tablonId : fragment.suerteId;
  return { source: 'master', layerId: model.id, kind: level, id };
}

export function legacySelectionForFocus(focus: SelectionEntry | null): { selection: Selection; genericSelection: { layerId: string; featureId: string } | null } {
  if (!focus) return { selection: null, genericSelection: null };
  return focus.source === 'generic'
    ? { selection: null, genericSelection: { layerId: focus.layerId, featureId: focus.id } }
    : { selection: { kind: focus.kind, id: focus.id }, genericSelection: null };
}

export function selectedFragmentIdsForSet(model: LayerModel | null, selectionSet: SelectionSet): ReadonlySet<string> {
  const result = new Set<string>();
  if (!model || selectionSet.layerId !== model.id) return result;
  selectionSet.entries.forEach((entry) => {
    if (entry.source !== 'master') return;
    if (entry.kind === 'fragment') result.add(entry.id);
    else {
      const entity = entry.kind === 'finca' ? model.fincas.get(entry.id) : entry.kind === 'suerte' ? model.suertes.get(entry.id) : model.tablones.get(entry.id);
      entity?.fragmentIds.forEach((id) => result.add(id));
    }
  });
  return result;
}

export function selectionEntriesInExtent(layer: VectorLayerModel, extent: Extent, level: SelectionLevel, allowedMasterFragments?: ReadonlySet<string>): SelectionEntry[] {
  if (layer.kind !== 'master') {
    return layer.features.flatMap((record) => record.feature.getGeometry()?.intersectsExtent(extent)
      ? [{ source: 'generic' as const, layerId: layer.id, kind: 'feature' as const, id: record.id }]
      : []);
  }
  const model = layer.masterModel;
  if (!model) return [];
  return model.fragments.flatMap((fragment) => (!allowedMasterFragments || allowedMasterFragments.has(fragment.id)) && fragment.feature.getGeometry()?.intersectsExtent(extent)
    ? [masterEntryForFragment(model, fragment.id, level)!]
    : []).filter((entry): entry is SelectionEntry => Boolean(entry));
}

export function recordsForSelection(layer: VectorLayerModel, selectionSet: SelectionSet): VectorFeatureRecord[] {
  if (selectionSet.layerId !== layer.id || !selectionSet.entries.length) return [];
  if (layer.kind !== 'master') return selectionSet.entries.flatMap((entry) => {
    const record = entry.source === 'generic' ? layer.featuresById.get(entry.id) : undefined;
    return record ? [record] : [];
  });
  const fragmentIds = selectedFragmentIdsForSet(layer.masterModel ?? null, selectionSet);
  return (layer.masterModel?.fragments ?? []).filter((fragment) => fragmentIds.has(fragment.id)).map(({ id, feature, attributes }) => ({ id, feature, attributes }));
}

export const selectionSetFromSelection = (layerId: string | null, selection: Selection): SelectionSet => {
  if (!layerId || !selection) return emptySelectionSet();
  const entry: SelectionEntry = { source: 'master', layerId, kind: selection.kind, id: selection.id };
  return { layerId, entries: [entry], focus: entry };
};
